import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const OAUTH_LOGIN_URL = `${API_BASE_URL}/oauth2/authorization/google`;

/**
 * Redirect the browser (not an XHR) to the OAuth login page.
 * Using window.location.href here is intentional — the browser
 * follows the 302 chain as a normal page navigation, which works.
 * An XHR/fetch cannot follow cross-origin redirects (CORS block).
 */
export function redirectToLogin(returnTo) {
  if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
    sessionStorage.setItem('authReturnTo', returnTo);
  } else if (!sessionStorage.getItem('authReturnTo')) {
    sessionStorage.setItem('authReturnTo', `${window.location.pathname}${window.location.search}`);
  }
  localStorage.removeItem('authToken');
  window.location.href = OAUTH_LOGIN_URL;
}

const api = axios.create({
  baseURL: API_BASE_URL,
});

function redirectForAuthentication(error) {
  const requestedDish = error.config?.params?.dishName;
  const requestPath = error.config?.url?.split('?')[0].replace(/\/+$/, '');
  if (requestPath?.endsWith('/recipe') && typeof requestedDish === 'string' && requestedDish.trim()) {
    sessionStorage.setItem('pendingRecipeDish', requestedDish);
    redirectToLogin('/dish');
    return;
  }
  redirectToLogin();
}

// ── Request interceptor — attach JWT ─────────────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor — handle auth failures ───────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    // 403 means the request is forbidden, not that the user needs to log in.
    // Redirecting on every 403 hides the original API failure and can start
    // an unnecessary OAuth flow.
    if (status === 401) {
      redirectForAuthentication(error);
      return new Promise(() => {}); // Never resolve — page is navigating away
    }

    // No response at all = network/CORS error.
    // This happens when Spring Boot returns a 302 → Google OAuth URL
    // and the browser follows it as an XHR, hitting Google's CORS block.
    // Only treat this as an auth error when we had a stored token
    // (i.e., this was a protected-route call, not just a connectivity issue).
    if (!error.response && localStorage.getItem('authToken')) {
      redirectForAuthentication(error);
      return new Promise(() => {});
    }

    return Promise.reject(error);
  }
);

export default api;
